import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import "./GoldTrading.css";

const INITIAL_ACCOUNT = {
  balance: 10000,
  equity: 10000,
  floatingProfit: 0,
  realizedProfit: 0,
};

export default function GoldTrading() {
  const chartRef = useRef(null);

  const [account, setAccount] =
    useState(INITIAL_ACCOUNT);

  const [price, setPrice] = useState(null);
  const [bid, setBid] = useState(null);
  const [ask, setAsk] = useState(null);

  const [lotSize, setLotSize] =
    useState(0.10);

  const [trades, setTrades] =
    useState([]);

  const [history, setHistory] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  /* =====================================================
     AI GOLD BOT STATE
  ===================================================== */

  const [aiBotEnabled, setAiBotEnabled] =
    useState(false);

  const [aiBotLoading, setAiBotLoading] =
    useState(false);

  const [aiBotSignal, setAiBotSignal] =
    useState(null);


  const [historyLoading, setHistoryLoading] =
    useState(false);

  const [errorMessage, setErrorMessage] =
    useState("");

  const token =
    localStorage.getItem("token");

  /* =====================================================
     API
  ===================================================== */

  const api = useCallback(
    async (url, options = {}) => {
      const response = await fetch(
        url,
        {
          ...options,
          headers: {
            "Content-Type":
              "application/json",

            ...(token
              ? {
                  Authorization:
                    `Bearer ${token}`,
                }
              : {}),

            ...(options.headers || {}),
          },
        }
      );

      let data = {};

      try {
        data =
          await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
            "Request failed"
        );
      }

      return data;
    },
    [token]
  );

  /* =====================================================
     AI GOLD BOT STATUS
  ===================================================== */

  const loadAiBotStatus = useCallback(
    async () => {
      try {
        const data =
          await api(
            "/api/demo-trading/bot/status"
          );

        if (
          data &&
          data.success
        ) {
          setAiBotEnabled(
            Boolean(
              data.bot?.enabled
            )
          );

          setAiBotSignal(
            data.signal || null
          );
        }
      } catch (error) {
        console.error(
          "AI bot status error:",
          error
        );
      }
    },
    [api]
  );

  /* =====================================================
     AI GOLD BOT ON / OFF
  ===================================================== */

  const toggleAiBot = async () => {
    try {
      setAiBotLoading(true);

      const endpoint =
        aiBotEnabled
          ? "/api/demo-trading/bot/stop"
          : "/api/demo-trading/bot/start";

      const options = {
        method: "POST",
      };

      if (!aiBotEnabled) {
        options.body =
          JSON.stringify({
            lotSize:
              Number(lotSize) || 0.01,
          });
      }

      const data =
        await api(
          endpoint,
          options
        );

      if (
        !data ||
        !data.success
      ) {
        throw new Error(
          data?.message ||
            "Unable to change AI bot status"
        );
      }

      setAiBotEnabled(
        Boolean(
          data.bot?.enabled
        )
      );

      await loadAiBotStatus();

    } catch (error) {
      console.error(
        "AI bot error:",
        error
      );

      setErrorMessage(
        error.message ||
          "Unable to change AI bot status"
      );

    } finally {
      setAiBotLoading(false);
    }
  };

  /* =====================================================
     LOAD ACCOUNT
  ===================================================== */

  const loadAccount =
    useCallback(async () => {
      try {
        const data =
          await api(
            "/api/demo-trading/account"
          );

        if (!data.success) {
          throw new Error(
            data.message ||
              "Unable to load demo account"
          );
        }

        setAccount(
          data.account ||
            INITIAL_ACCOUNT
        );

        setTrades(
          Array.isArray(
            data.trades
          )
            ? data.trades
            : []
        );

        const quote =
          data.quote || {};

        if (
          Number.isFinite(
            Number(quote.price)
          )
        ) {
          setPrice(
            Number(quote.price)
          );
        }

        if (
          Number.isFinite(
            Number(quote.bid)
          )
        ) {
          setBid(
            Number(quote.bid)
          );
        }

        if (
          Number.isFinite(
            Number(quote.ask)
          )
        ) {
          setAsk(
            Number(quote.ask)
          );
        }

        setErrorMessage("");
      } catch (error) {
        console.error(
          "Demo account:",
          error
        );

        setErrorMessage(
          error.message ||
            "Unable to load demo account"
        );
      }
    }, [api]);

  /* =====================================================
     LOAD HISTORY
  ===================================================== */

  const loadHistory =
    useCallback(async () => {
      try {
        setHistoryLoading(true);

        const data =
          await api(
            "/api/demo-trading/history"
          );

        if (data.success) {
          setHistory(
            Array.isArray(
              data.trades
            )
              ? data.trades
              : []
          );
        }
      } catch (error) {
        console.error(
          "Trade history:",
          error
        );
      } finally {
        setHistoryLoading(false);
      }
    }, [api]);

  /* =====================================================
     INITIAL LOAD
  ===================================================== */

  useEffect(() => {
    loadAccount();
    loadHistory();
    loadAiBotStatus();

    const timer =
      setInterval(() => {
        loadAccount();
      }, 3000);

    return () => {
      clearInterval(timer);
    };
  }, [
    loadAccount,
    loadHistory,
    loadAiBotStatus,
  ]);

  /* =====================================================
     LIVE GOLD SOCKET
  ===================================================== */

  useEffect(() => {
    let socket = null;
    let mounted = true;

    const connectSocket =
      async () => {
        try {
          const {
            io,
          } = await import(
            "socket.io-client"
          );

          if (!mounted) return;

          socket = io(
            window.location.origin,
            {
              path:
                "/socket.io",

              transports: [
                "websocket",
                "polling",
              ],
            }
          );

          socket.on(
            "connect",
            () => {
              console.log(
                "🥇 Gold socket connected"
              );
            }
          );

          socket.on(
            "gold_price",
            (data) => {
              if (!data) return;

              const livePrice =
                Number(data.price);

              const liveBid =
                Number(data.bid);

              const liveAsk =
                Number(data.ask);

              if (
                Number.isFinite(
                  livePrice
                )
              ) {
                setPrice(
                  livePrice
                );
              }

              if (
                Number.isFinite(
                  liveBid
                )
              ) {
                setBid(
                  liveBid
                );
              }

              if (
                Number.isFinite(
                  liveAsk
                )
              ) {
                setAsk(
                  liveAsk
                );
              }
            }
          );

          socket.on(
            "disconnect",
            () => {
              console.log(
                "🥇 Gold socket disconnected"
              );
            }
          );
        } catch (error) {
          console.error(
            "Socket connection error:",
            error
          );
        }
      };

    connectSocket();

    return () => {
      mounted = false;

      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  /* =====================================================
     TRADINGVIEW CHART
  ===================================================== */

  useEffect(() => {
    const container =
      chartRef.current;

    if (!container) {
      return undefined;
    }

    container.innerHTML = "";

    const widget =
      document.createElement(
        "div"
      );

    widget.className =
      "tradingview-widget-container";

    widget.style.width =
      "100%";

    widget.style.height =
      "100%";

    widget.style.minHeight =
      "450px";

    const script =
      document.createElement(
        "script"
      );

    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";

    script.type =
      "text/javascript";

    script.async = true;

    script.text =
      JSON.stringify({
        autosize: true,

        symbol:
          "OANDA:XAUUSD",

        interval:
          "15",

        timezone:
          "Asia/Kolkata",

        theme:
          "dark",

        style:
          "1",

        locale:
          "en",

        enable_publishing:
          false,

        allow_symbol_change:
          false,

        hide_side_toolbar:
          false,

        withdateranges:
          true,

        hide_top_toolbar:
          false,

        hide_legend:
          false,

        save_image:
          false,

        calendar:
          false,

        hide_volume:
          false,

        support_host:
          "https://www.tradingview.com",
      });

    widget.appendChild(
      script
    );

    container.appendChild(
      widget
    );

    return () => {
      if (container) {
        container.innerHTML =
          "";
      }
    };
  }, []);

  /* =====================================================
     OPEN TRADE
  ===================================================== */

  const openTrade =
    async (side) => {
      try {
        if (!price) {
          alert(
            "Live XAUUSD price is not available yet."
          );

          return;
        }

        const lots =
          Number(lotSize);

        if (
          !Number.isFinite(lots) ||
          lots < 0.01 ||
          lots > 100
        ) {
          alert(
            "Lot size must be between 0.01 and 100."
          );

          return;
        }

        setLoading(true);
        setErrorMessage("");

        const data =
          await api(
            "/api/demo-trading/open",
            {
              method:
                "POST",

              body:
                JSON.stringify({
                  side,
                  lotSize:
                    lots,
                }),
            }
          );

        if (!data.success) {
          throw new Error(
            data.message ||
              "Unable to open trade"
          );
        }

        const entryPrice =
          Number(
            data.trade?.entryPrice
          );

        alert(
          `${side} XAUUSD opened at $${entryPrice.toFixed(
            2
          )}`
        );

        await loadAccount();
        await loadHistory();
      } catch (error) {
        console.error(
          "Open trade error:",
          error
        );

        alert(
          error.message ||
            "Unable to open trade"
        );
      } finally {
        setLoading(false);
      }
    };

  /* =====================================================
     CLOSE TRADE
  ===================================================== */

  const closeTrade =
    async (tradeId) => {
      try {
        setLoading(true);

        const data =
          await api(
            `/api/demo-trading/close/${tradeId}`,
            {
              method:
                "POST",
            }
          );

        if (!data.success) {
          throw new Error(
            data.message ||
              "Unable to close trade"
          );
        }

        const pnl =
          Number(
            data.profitLoss || 0
          );

        alert(
          `Trade closed. P/L: ${
            pnl >= 0 ? "+" : ""
          }$${pnl.toFixed(2)}`
        );

        await loadAccount();
        await loadHistory();
      } catch (error) {
        console.error(
          "Close trade error:",
          error
        );

        alert(
          error.message ||
            "Unable to close trade"
        );
      } finally {
        setLoading(false);
      }
    };

  /* =====================================================
     LIVE P/L
  ===================================================== */

  const calculateLivePnl =
    (trade) => {
      if (!price) return 0;

      const current =
        Number(price);

      const entry =
        Number(
          trade.entryPrice || 0
        );

      const lots =
        Number(
          trade.lotSize || 0
        );

      if (
        !entry ||
        !lots
      ) {
        return 0;
      }

      const difference =
        trade.side === "BUY"
          ? current - entry
          : entry - current;

      return (
        difference *
        100 *
        lots
      );
    };

  /* =====================================================
     FORMAT
  ===================================================== */

  const money =
    (value) => {
      const number =
        Number(value) || 0;

      return `$${number.toLocaleString(
        "en-US",
        {
          minimumFractionDigits:
            2,

          maximumFractionDigits:
            2,
        }
      )}`;
    };

  const priceText =
    (value) => {
      if (
        !Number.isFinite(
          Number(value)
        )
      ) {
        return "--";
      }

      return Number(value).toFixed(2);
    };

  const changeLot =
    (amount) => {
      setLotSize(
        (current) => {
          const value =
            Number(current) ||
            0.01;

          const next =
            value + amount;

          if (next < 0.01) {
            return 0.01;
          }

          if (next > 100) {
            return 100;
          }

          return Number(
            next.toFixed(2)
          );
        }
      );
    };

  /* =====================================================
     NAVIGATION
  ===================================================== */

  const go =
    (path) => {
      window.location.href =
        path;
    };

  /* =====================================================
     UI
  ===================================================== */

  return (
    <div className="gold-page">

      {/* =================================================
          SIDEBAR
      ================================================= */}

      <aside className="gold-sidebar">

        <div className="gold-brand">
          <div className="gold-brand-main">
            ALGO<span>XPO</span>
          </div>

          <div className="gold-brand-sub">
            GOLD DEMO TRADING
          </div>
        </div>

        <div className="gold-side-group">

          <div className="gold-side-title">
            TRADING
          </div>

          <button
            className="gold-side-item"
            onClick={() =>
              go("/dashboard")
            }
          >
            <span>⌂</span>
            <span>Dashboard</span>
          </button>

          <button
            className="gold-side-item active"
          >
            <span>🥇</span>
            <span>Gold Trading</span>
            <b>LIVE</b>
          </button>

          <button
            className="gold-side-item"
            onClick={() =>
              document
                .getElementById(
                  "gold-market-section"
                )
                ?.scrollIntoView({
                  behavior:
                    "smooth",
                })
            }
          >
            <span>⌁</span>
            <span>Live Market</span>
          </button>

          <button
            className="gold-side-item"
            onClick={() =>
              window.open(
                "https://www.tradingview.com/",
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            <span>◈</span>
            <span>TradingView</span>
          </button>

        </div>

        <div className="gold-side-group">

          <div className="gold-side-title">
            ACCOUNT
          </div>

          <button
            className="gold-side-item"
            onClick={() =>
              alert(
                "Demo Account"
              )
            }
          >
            <span>▣</span>
            <span>Demo Account</span>
          </button>

          <button
            className="gold-side-item"
            onClick={() =>
              document
                .getElementById(
                  "gold-history"
                )
                ?.scrollIntoView({
                  behavior:
                    "smooth",
                })
            }
          >
            <span>▤</span>
            <span>Income History</span>
          </button>

          <button
            className="gold-side-item"
            onClick={() =>
              document
                .getElementById(
                  "gold-rules"
                )
                ?.scrollIntoView({
                  behavior:
                    "smooth",
                })
            }
          >
            <span>⌁</span>
            <span>ROI Report</span>
          </button>

        </div>

        <div className="gold-side-group">

          <div className="gold-side-title">
            ANALYTICS
          </div>

          <button
            className="gold-side-item"
            onClick={() =>
              alert(
                "Demo Performance"
              )
            }
          >
            <span>▥</span>
            <span>Performance</span>
          </button>

          <button
            className="gold-side-item"
            onClick={() =>
              document
                .getElementById(
                  "gold-history"
                )
                ?.scrollIntoView({
                  behavior:
                    "smooth",
                })
            }
          >
            <span>▤</span>
            <span>Trade History</span>
          </button>

        </div>

        <div className="gold-side-group">

          <div className="gold-side-title">
            SETTINGS
          </div>

          <button
            className="gold-side-item"
            onClick={() =>
              alert(
                "Profile"
              )
            }
          >
            <span>♙</span>
            <span>Profile</span>
          </button>

          <button
            className="gold-side-item"
            onClick={() =>
              alert(
                "Security"
              )
            }
          >
            <span>♧</span>
            <span>Security</span>
          </button>

          <button
            className="gold-side-item logout"
            onClick={() => {
              localStorage.removeItem(
                "token"
              );

              window.location.href =
                "/login";
            }}
          >
            <span>↪</span>
            <span>Logout</span>
          </button>

        </div>

        <div className="gold-sidebar-upgrade">

          <strong>
            Upgrade to Real Account
          </strong>

          <small>
            Trade with real money
          </small>

          <button
            onClick={() =>
              alert(
                "Real trading will be available soon."
              )
            }
          >
            ★ Upgrade Now
          </button>

        </div>

      </aside>

      {/* =================================================
          MAIN
      ================================================= */}

      <main className="gold-terminal">

        {/* TOP BAR */}

        <header className="gold-topbar">

          <div className="gold-top-left">

            <span className="gold-market-dot" />

            <span>
              Market Live
            </span>

          </div>

          <div className="gold-user">

            <div className="gold-notification">
              ●
            </div>

            <div>
              <strong>
                Demo Trader
              </strong>

              <small>
                Premium Trader
              </small>
            </div>

            <div className="gold-avatar">
              DT
            </div>

          </div>

        </header>

        {/* MARKET BAR */}

        <section
          id="gold-market-section"
          className="gold-market-bar"
        >

          <div className="gold-symbol">

            <div>
              XAUUSD <span>☆</span>
            </div>

            <small>
              Gold / US Dollar
            </small>

          </div>

          <div className="gold-main-price">

            <strong>
              {price
                ? money(price)
                : "Loading..."}
            </strong>

            <small>
              LIVE XAUUSD
            </small>

          </div>

          <div className="gold-market-metric">
            <span>
              24H HIGH
            </span>
            <strong>
              —
            </strong>
          </div>

          <div className="gold-market-metric">
            <span>
              24H LOW
            </span>
            <strong>
              —
            </strong>
          </div>

          <div className="gold-market-metric">
            <span>
              24H CHANGE
            </span>
            <strong className="profit">
              LIVE
            </strong>
          </div>

          <div className="gold-market-metric">
            <span>
              BID
            </span>
            <strong className="sell-text">
              {priceText(bid)}
            </strong>
          </div>

          <div className="gold-market-metric">
            <span>
              ASK
            </span>
            <strong className="profit">
              {priceText(ask)}
            </strong>
          </div>

          <div className="gold-market-metric">
            <span>
              SPREAD
            </span>
            <strong>
              {bid && ask
                ? (
                    Number(ask) -
                    Number(bid)
                  ).toFixed(2)
                : "0.00"}
            </strong>
          </div>

        </section>

        {/* ERROR */}

        {errorMessage && (
          <div className="gold-error">
            {errorMessage}
          </div>
        )}

        {/* ACCOUNT */}

        <section className="gold-account-strip">

          <div className="gold-account-card main">
            <span>
              DEMO ACCOUNT
            </span>

            <strong>
              {money(account.balance)}
            </strong>

            <small>
              Demo Balance
            </small>
          </div>

          <div className="gold-account-card">
            <span>
              EQUITY
            </span>

            <strong>
              {money(account.equity)}
            </strong>

            <small>
              Account Equity
            </small>
          </div>

          <div className="gold-account-card">
            <span>
              FLOATING P/L
            </span>

            <strong
              className={
                Number(
                  account.floatingProfit ||
                    0
                ) >= 0
                  ? "profit"
                  : "loss"
              }
            >
              {Number(
                account.floatingProfit ||
                  0
              ) >= 0
                ? "+"
                : ""}

              {money(
                account.floatingProfit
              )}
            </strong>

            <small>
              Open Positions
            </small>
          </div>

          <div className="gold-account-card">
            <span>
              REALIZED P/L
            </span>

            <strong
              className={
                Number(
                  account.realizedProfit ||
                    0
                ) >= 0
                  ? "profit"
                  : "loss"
              }
            >
              {Number(
                account.realizedProfit ||
                  0
              ) >= 0
                ? "+"
                : ""}

              {money(
                account.realizedProfit
              )}
            </strong>

            <small>
              Closed Trades
            </small>
          </div>

        </section>

        {/* =================================================
            CHART + ORDER
        ================================================= */}

        <section className="gold-terminal-grid">

          {/* CHART */}

          <div className="gold-chart-card">

            <div className="gold-chart-header">

              <div>
                <strong>
                  XAUUSD
                </strong>

                <span>
                  Gold Spot / U.S. Dollar
                </span>
              </div>

              <div className="gold-timeframes">

                <button>
                  1m
                </button>

                <button>
                  5m
                </button>

                <button className="selected">
                  15m
                </button>

                <button>
                  1h
                </button>

                <button>
                  4h
                </button>

              </div>

            </div>

            <div
              ref={chartRef}
              className="gold-chart"
            />

          </div>

          {/* ORDER PANEL */}

          <div className="gold-order-card">

            <div className="gold-order-tabs">

              <button className="active">
                Market Order
              </button>

              <button>
                Pending Order
              </button>

            </div>

            <div className="gold-order-body">

              <div className="gold-order-title">
                LOT SIZE
              </div>

              <div className="gold-lot-control">

                <button
                  type="button"
                  onClick={() =>
                    changeLot(-0.01)
                  }
                >
                  −
                </button>

                <strong>
                  {Number(
                    lotSize
                  ).toFixed(2)}
                </strong>

                <button
                  type="button"
                  onClick={() =>
                    changeLot(0.01)
                  }
                >
                  +
                </button>

              </div>

              <div className="gold-lot-info">

                <span>
                  Min: 0.01
                </span>

                <span>
                  Step: 0.01
                </span>

                <span>
                  Max: 100
                </span>

              </div>

              <div className="gold-order-values">

                <div>
                  <span>
                    VALUE
                  </span>

                  <strong>
                    {price
                      ? money(
                          Number(price) *
                            100 *
                            Number(lotSize)
                        )
                      : "--"}
                  </strong>
                </div>

                <div>
                  <span>
                    MARGIN
                  </span>

                  <strong>
                    {price
                      ? money(
                          Number(price) *
                            Number(lotSize) *
                            0.01
                        )
                      : "--"}
                  </strong>
                </div>

              </div>

              {/* =========================
                  AI GOLD BOT
              ========================= */}

              <div className="gold-ai-bot-card">

                <div className="gold-ai-bot-head">

                  <div>
                    <strong>
                      🤖 AI GOLD BOT
                    </strong>

                    <small>
                      XAUUSD automated demo strategy
                    </small>
                  </div>

                  <span
                    className={
                      aiBotEnabled
                        ? "gold-ai-status on"
                        : "gold-ai-status off"
                    }
                  >
                    {aiBotEnabled
                      ? "● ON"
                      : "● OFF"}
                  </span>

                </div>

                {aiBotSignal && (
                  <div className="gold-ai-signal">

                    <span>
                      AI SIGNAL
                    </span>

                    <strong
                      className={
                        aiBotSignal.signal === "BUY"
                          ? "profit"
                          : aiBotSignal.signal === "SELL"
                          ? "loss"
                          : ""
                      }
                    >
                      {aiBotSignal.signal || "WAIT"}
                    </strong>

                  </div>
                )}

                <button
                  type="button"
                  className={
                    aiBotEnabled
                      ? "gold-ai-stop"
                      : "gold-ai-start"
                  }
                  disabled={aiBotLoading}
                  onClick={toggleAiBot}
                >
                  {aiBotLoading
                    ? "PLEASE WAIT..."
                    : aiBotEnabled
                    ? "🛑 TURN OFF AI BOT"
                    : "🟢 TURN ON AI BOT"}
                </button>

              </div>

              <div className="gold-big-buttons">

                <button
                  className="gold-buy"
                  disabled={
                    loading ||
                    !price
                  }
                  onClick={() =>
                    openTrade("BUY")
                  }
                >
                  <span>
                    BUY
                  </span>

                  <strong>
                    {priceText(
                      ask || price
                    )}
                  </strong>
                </button>

                <button
                  className="gold-sell"
                  disabled={
                    loading ||
                    !price
                  }
                  onClick={() =>
                    openTrade("SELL")
                  }
                >
                  <span>
                    SELL
                  </span>

                  <strong>
                    {priceText(
                      bid || price
                    )}
                  </strong>
                </button>

              </div>

              <div className="gold-contract-box">

                <div className="gold-bars">
                  🪙
                </div>

                <div>
                  <strong>
                    1 Lot = 100 Troy Ounces
                  </strong>

                  <small>
                    Pip value for 0.01 lot =
                    $1.00
                  </small>
                </div>

              </div>

              <div className="gold-demo-warning">

                <strong>
                  DEMO ACCOUNT
                </strong>

                <p>
                  Virtual funds only.
                  Real wallet funds are
                  never affected.
                </p>

              </div>

            </div>

          </div>

        </section>

        {/* =================================================
            POSITIONS + HISTORY
        ================================================= */}

        <section className="gold-bottom-grid">

          {/* OPEN POSITIONS */}

          <div className="gold-positions-card">

            <div className="gold-section-title">

              <div>
                <strong>
                  OPEN POSITIONS
                </strong>

                <span>
                  ({trades.length})
                </span>
              </div>

              <span className="open-badge">
                {trades.length} Open
              </span>

            </div>

            <div className="gold-table-wrap">

              <table className="gold-table">

                <thead>
                  <tr>
                    <th>
                      Symbol
                    </th>

                    <th>
                      Type
                    </th>

                    <th>
                      Lot Size
                    </th>

                    <th>
                      Entry Price
                    </th>

                    <th>
                      Current Price
                    </th>

                    <th>
                      P/L
                    </th>

                    <th>
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>

                  {trades.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="gold-no-data"
                      >
                        No open positions
                      </td>
                    </tr>
                  ) : (
                    trades.map(
                      (trade) => {
                        const pnl =
                          calculateLivePnl(
                            trade
                          );

                        return (
                          <tr
                            key={
                              trade._id
                            }
                          >
                            <td>
                              <strong>
                                XAUUSD
                              </strong>
                            </td>

                            <td
                              className={
                                trade.side ===
                                "BUY"
                                  ? "trade-buy"
                                  : "trade-sell"
                              }
                            >
                              {trade.side}
                            </td>

                            <td>
                              {Number(
                                trade.lotSize
                              ).toFixed(2)}
                            </td>

                            <td>
                              {priceText(
                                trade.entryPrice
                              )}
                            </td>

                            <td>
                              {priceText(
                                price
                              )}
                            </td>

                            <td
                              className={
                                pnl >= 0
                                  ? "profit"
                                  : "loss"
                              }
                            >
                              {pnl >= 0
                                ? "+"
                                : ""}

                              {money(pnl)}
                            </td>

                            <td>
                              <button
                                className="gold-close-btn"
                                disabled={
                                  loading
                                }
                                onClick={() =>
                                  closeTrade(
                                    trade._id
                                  )
                                }
                              >
                                CLOSE
                              </button>
                            </td>
                          </tr>
                        );
                      }
                    )
                  )}

                </tbody>

              </table>

            </div>

            <div className="gold-floating-footer">

              <span>
                Total Floating P/L
              </span>

              <strong
                className={
                  Number(
                    account.floatingProfit ||
                      0
                  ) >= 0
                    ? "profit"
                    : "loss"
                }
              >
                {Number(
                  account.floatingProfit ||
                    0
                ) >= 0
                  ? "+"
                  : ""}

                {money(
                  account.floatingProfit
                )}
              </strong>

            </div>

          </div>

          {/* HISTORY */}

          <div
            id="gold-history"
            className="gold-history-card"
          >

            <div className="gold-section-title">

              <div>
                <strong>
                  TRADE HISTORY
                </strong>
              </div>

              <button
                className="history-view"
                onClick={
                  loadHistory
                }
                disabled={
                  historyLoading
                }
              >
                {historyLoading
                  ? "Loading..."
                  : "Refresh"}
              </button>

            </div>

            <div className="gold-table-wrap">

              <table className="gold-table">

                <thead>
                  <tr>
                    <th>
                      Symbol
                    </th>

                    <th>
                      Type
                    </th>

                    <th>
                      Lot
                    </th>

                    <th>
                      Entry
                    </th>

                    <th>
                      Exit
                    </th>

                    <th>
                      P/L
                    </th>

                    <th>
                      Time
                    </th>
                  </tr>
                </thead>

                <tbody>

                  {history.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="gold-no-data"
                      >
                        No trades yet
                      </td>
                    </tr>
                  ) : (
                    history.map(
                      (trade) => (
                        <tr
                          key={
                            trade._id
                          }
                        >
                          <td>
                            <strong>
                              XAUUSD
                            </strong>
                          </td>

                          <td
                            className={
                              trade.side ===
                              "BUY"
                                ? "trade-buy"
                                : "trade-sell"
                            }
                          >
                            {trade.side}
                          </td>

                          <td>
                            {Number(
                              trade.lotSize
                            ).toFixed(2)}
                          </td>

                          <td>
                            {priceText(
                              trade.entryPrice
                            )}
                          </td>

                          <td>
                            {trade.exitPrice
                              ? priceText(
                                  trade.exitPrice
                                )
                              : "--"}
                          </td>

                          <td
                            className={
                              Number(
                                trade.profitLoss ||
                                  0
                              ) >= 0
                                ? "profit"
                                : "loss"
                            }
                          >
                            {Number(
                              trade.profitLoss ||
                                0
                            ) >= 0
                              ? "+"
                              : ""}

                            {money(
                              trade.profitLoss
                            )}
                          </td>

                          <td>
                            {trade.closedAt
                              ? new Date(
                                  trade.closedAt
                                ).toLocaleString(
                                  "en-IN",
                                  {
                                    day:
                                      "2-digit",
                                    month:
                                      "short",
                                    hour:
                                      "2-digit",
                                    minute:
                                      "2-digit",
                                  }
                                )
                              : "--"}
                          </td>
                        </tr>
                      )
                    )
                  )}

                </tbody>

              </table>

            </div>

          </div>

        </section>

        {/* =================================================
            RULES
        ================================================= */}

        <section
          id="gold-rules"
          className="gold-rules-card"
        >

          <div className="gold-section-title">

            <div>
              <strong>
                DEMO TRADING RULES
              </strong>
            </div>

          </div>

          <div className="gold-rule-grid">

            <div>
              <span>
                STARTING BALANCE
              </span>

              <strong>
                $10,000
              </strong>
            </div>

            <div>
              <span>
                SYMBOL
              </span>

              <strong>
                XAUUSD
              </strong>
            </div>

            <div>
              <span>
                CONTRACT
              </span>

              <strong>
                100 OZ / LOT
              </strong>
            </div>

            <div>
              <span>
                MIN LOT
              </span>

              <strong>
                0.01
              </strong>
            </div>

          </div>

          <p className="gold-disclaimer">
            This is a virtual demo
            trading environment.
            All balances and profits
            are simulated and do not
            represent real funds.
          </p>

        </section>

        {/* =================================================
            TICKER
        ================================================= */}

        <div className="gold-ticker">

          <div>
            <strong>
              XAUUSD
            </strong>

            <span>
              {priceText(price)}
            </span>

            <b>
              LIVE
            </b>
          </div>

          <div>
            <strong>
              BTCUSD
            </strong>

            <span>—</span>
          </div>

          <div>
            <strong>
              EURUSD
            </strong>

            <span>—</span>
          </div>

          <div>
            <strong>
              GBPUSD
            </strong>

            <span>—</span>
          </div>

          <div>
            <strong>
              USDJPY
            </strong>

            <span>—</span>
          </div>

          <small>
            Powered by TradingView
          </small>

        </div>

      </main>

    </div>
  );
}
